import type { CSSProperties } from 'react';

import type { IMatchScheduleItem } from '../../../../../types/schedule';
import {
  getScheduleBodyYFromSlotIndex,
  getScheduleSlotIndexFromDayStartMs,
} from './scheduleMatchLayout';

export const SCHEDULE_SLOT_HIGHLIGHT_COLOR = 'rgba(34, 197, 94, 0.2)';

export type SlotBandPx = { top: number; bottom: number };

/** Minimal match row shape for computing valid placement bands (grid highlight). */
export type PlacementHighlightMatchRange = {
  item: IMatchScheduleItem;
  startMs: number;
  endMs: number;
};

export type ComputePlacementBandsParams = {
  dayStartMs: number;
  dayEndMs: number;
  matchRanges: PlacementHighlightMatchRange[];
  activeMatch: IMatchScheduleItem;
  durationMins: number;
  scale: number;
  /** Must match grid free-slot step (e.g. 5). */
  slotMinutes: number;
  msPerMinute: number;
  /** When set, only these start times are used (e.g. after conflict filter). */
  validStartsMs?: number[];
};

/** Start-slot bands where the match fits without overlapping other scheduled rows (active match excluded). */
export function computeValidPlacementBandsPx({
  dayStartMs,
  dayEndMs,
  matchRanges,
  activeMatch,
  durationMins,
  scale,
  slotMinutes,
  msPerMinute,
  validStartsMs: validStartsMsOverride,
}: ComputePlacementBandsParams): SlotBandPx[] {
  const excludeId = activeMatch.id;
  const occupied = matchRanges
    .filter((r) => r.item.id !== excludeId)
    .map((r) => ({ start: r.startMs, end: r.endMs }))
    .sort((a, b) => a.start - b.start);

  const slotStepMs = slotMinutes * msPerMinute;
  const durationMs = durationMins * msPerMinute;
  const maxStartMs = dayEndMs - durationMs;
  if (maxStartMs < dayStartMs) return [];

  const validStartsMs =
    validStartsMsOverride ??
    collectValidPlacementStartTimesMs({
      dayStartMs,
      maxStartMs,
      slotStepMs,
      durationMs,
      occupied,
    });

  if (validStartsMs.length === 0) return [];

  const slotIndex = (timeMs: number) =>
    getScheduleSlotIndexFromDayStartMs(timeMs, dayStartMs, slotMinutes, msPerMinute);

  const bands: SlotBandPx[] = [];
  let runStart = validStartsMs[0];
  let prev = validStartsMs[0];
  for (let i = 1; i < validStartsMs.length; i++) {
    const t = validStartsMs[i];
    if (t === prev + slotStepMs) {
      prev = t;
    } else {
      bands.push({
        top: getScheduleBodyYFromSlotIndex(slotIndex(runStart), scale),
        bottom: getScheduleBodyYFromSlotIndex(slotIndex(prev) + 1, scale),
      });
      runStart = t;
      prev = t;
    }
  }
  bands.push({
    top: getScheduleBodyYFromSlotIndex(slotIndex(runStart), scale),
    bottom: getScheduleBodyYFromSlotIndex(slotIndex(prev) + 1, scale),
  });
  return bands;
}

function collectValidPlacementStartTimesMs({
  dayStartMs,
  maxStartMs,
  slotStepMs,
  durationMs,
  occupied,
}: {
  dayStartMs: number;
  maxStartMs: number;
  slotStepMs: number;
  durationMs: number;
  occupied: { start: number; end: number }[];
}): number[] {
  const validStartsMs: number[] = [];
  for (let t = dayStartMs; t <= maxStartMs; t += slotStepMs) {
    const endT = t + durationMs;
    const overlaps = occupied.some((o) => t < o.end && endT > o.start);
    if (!overlaps) validStartsMs.push(t);
  }
  return validStartsMs;
}

/** Valid placement start times (ms) for keyboard navigation and band computation. */
export function computeValidPlacementStartTimesMs({
  dayStartMs,
  dayEndMs,
  matchRanges,
  activeMatch,
  durationMins,
  slotMinutes,
  msPerMinute,
}: Omit<ComputePlacementBandsParams, 'scale'>): number[] {
  const excludeId = activeMatch.id;
  const occupied = matchRanges
    .filter((r) => r.item.id !== excludeId)
    .map((r) => ({ start: r.startMs, end: r.endMs }))
    .sort((a, b) => a.start - b.start);

  const slotStepMs = slotMinutes * msPerMinute;
  const durationMs = durationMins * msPerMinute;
  const maxStartMs = dayEndMs - durationMs;
  if (maxStartMs < dayStartMs) return [];

  return collectValidPlacementStartTimesMs({
    dayStartMs,
    maxStartMs,
    slotStepMs,
    durationMs,
    occupied,
  });
}

export function buildColumnHighlightStyle(
  bands: SlotBandPx[],
  totalContentHeightPx: number,
  highlightColor: string
): CSSProperties {
  if (bands.length === 0 || totalContentHeightPx <= 0) {
    return { backgroundImage: 'none' };
  }
  const sorted = [...bands].sort((a, b) => a.top - b.top);
  const parts: string[] = [];
  let y = 0;
  for (const b of sorted) {
    const t0 = Math.max(0, Math.min(b.top, totalContentHeightPx));
    const t1 = Math.max(0, Math.min(b.bottom, totalContentHeightPx));
    if (t1 <= t0) continue;
    if (y < t0) {
      parts.push(`transparent ${y}px`, `transparent ${t0}px`);
    }
    parts.push(`${highlightColor} ${t0}px`, `${highlightColor} ${t1}px`);
    y = t1;
  }
  if (y < totalContentHeightPx) {
    parts.push(`transparent ${y}px`, `transparent ${totalContentHeightPx}px`);
  }
  if (parts.length === 0) {
    return { backgroundImage: 'none' };
  }
  return {
    backgroundImage: `linear-gradient(to bottom, ${parts.join(', ')})`,
    backgroundRepeat: 'no-repeat',
  };
}
